(() => {
  const mobile = window.matchMedia('(max-width: 959px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new WeakMap();
  const overlayCandidates = 'dialog, cart-drawer, cart-notification, modal-dialog, [data-theme-media-lightbox], [data-theme-quick-view-dialog]';
  const overlaySelector = 'dialog[open], cart-drawer.active, cart-notification.active, modal-dialog[open], [data-theme-media-lightbox]:not([hidden]), [data-theme-quick-view-dialog][open]';

  function init(section) {
    if (controllers.has(section)) return;
    const bar = section.querySelector('[data-theme-pdp-sticky-purchase]');
    const button = bar?.querySelector('[data-theme-pdp-sticky-button]');
    const label = button?.querySelector('[data-theme-pdp-sticky-label]');
    const primary = section.querySelector('[data-theme-pdp-atc]');
    const form = primary?.form;
    if (!bar || !button || !label || !primary || !form) return;

    const abort = new AbortController();
    const { signal } = abort;
    const viewport = window.visualViewport;
    let frame = 0;
    let exposed = false;
    let visible = false;
    let hideTimer = 0;

    const finishHide = () => {
      if (!visible) bar.hidden = true;
      hideTimer = 0;
    };

    const setVisible = (shouldShow) => {
      if (visible === shouldShow) return;
      visible = shouldShow;
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = 0;
      }
      if (shouldShow) {
        const wasHidden = bar.hidden;
        bar.hidden = false;
        bar.inert = false;
        bar.removeAttribute('aria-hidden');
        // Give the newly displayed bar its offscreen starting state before revealing it.
        if (wasHidden) bar.getBoundingClientRect();
        bar.classList.add('is-visible');
        reportExposure();
      } else {
        bar.classList.remove('is-visible');
        bar.inert = true;
        bar.setAttribute('aria-hidden', 'true');
        if (reducedMotion.matches || !mobile.matches) finishHide();
        else hideTimer = setTimeout(finishHide, 260);
      }
      document.body.classList.toggle('theme-pdp-sticky-purchase-visible', shouldShow);
    };

    const reportExposure = () => {
      if (exposed) return;
      exposed = true;
      document.dispatchEvent(new CustomEvent('theme:pdp:sticky-exposure', {
        detail: { form, variantId: form.elements.id?.value || '' }
      }));
    };

    const hasBlockingOverlay = () =>
      Boolean(document.querySelector(overlaySelector)) ||
      document.body.classList.contains('overflow-hidden') ||
      document.body.classList.contains('theme-quick-view-open') ||
      document.body.classList.contains('theme-lightbox-open') ||
      document.body.classList.contains('theme-contact-popup-open');

    const sync = () => {
      frame = 0;
      if (!section.isConnected) {
        destroy();
        return;
      }

      const viewportTop = viewport?.offsetTop || 0;
      const viewportHeight = viewport?.height || window.innerHeight;
      const keyboardOpen = Boolean(viewport && viewportHeight < window.innerHeight - 120);
      const focusedEditor = document.activeElement?.matches?.('input, textarea, select, [contenteditable="true"]');
      const passedPrimary = primary.getBoundingClientRect().bottom <= viewportTop + 1;
      const shouldShow = mobile.matches && passedPrimary && !keyboardOpen && !focusedEditor && !hasBlockingOverlay();

      const nextLabel = primary.querySelector('[data-theme-pdp-atc-label]')?.textContent?.trim() || primary.dataset.themeDefaultLabel || 'Add to cart';
      if (label.textContent !== nextLabel) label.textContent = nextLabel;
      setVisible(shouldShow);
      if (shouldShow) {
        const height = `${Math.ceil(bar.getBoundingClientRect().height)}px`;
        if (document.body.style.getPropertyValue('--theme-pdp-sticky-purchase-height') !== height) {
          document.body.style.setProperty('--theme-pdp-sticky-purchase-height', height);
        }
      } else if (document.body.style.getPropertyValue('--theme-pdp-sticky-purchase-height')) {
        document.body.style.removeProperty('--theme-pdp-sticky-purchase-height');
      }
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(sync);
    };

    const onClick = () => {
      if (!mobile.matches || !visible) return;
      const target = form.querySelector('[data-theme-pdp-color-group], [data-theme-pdp-size-group], [data-theme-quantity-control]') || form;
      // Upward scrolling reveals the mobile header, so reserve its full height
      // even while it is currently translated out of view.
      const headerHeight = document.querySelector('.theme-site-header')?.getBoundingClientRect().height || 0;
      const purchaseTop = window.scrollY + form.getBoundingClientRect().top;
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      window.scrollTo({ top: Math.max(0, purchaseTop - headerHeight - 12), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      schedule();
    };

    const onPurchase = (event) => {
      if (event.detail?.form !== form) return;
      schedule();
      if (event.detail.phase !== 'result' || event.detail.outcome === 'accepted') return;
      const error = section.querySelector('.product-form__error-message-wrapper:not([hidden])');
      if (error) {
        error.setAttribute('tabindex', '-1');
        error.scrollIntoView({ block: 'center' });
        error.focus({ preventScroll: true });
      }
    };

    const primaryObserver = new MutationObserver(schedule);
    primaryObserver.observe(primary, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'], childList: true, characterData: true, subtree: true });
    const observedOverlays = new WeakSet();
    const overlayObserver = new MutationObserver((mutations) => {
      let changed = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') changed = true;
        mutation.addedNodes?.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches(overlayCandidates)) {
            observeOverlay(node);
            changed = true;
          }
          node.querySelectorAll(overlayCandidates).forEach((overlay) => {
            observeOverlay(overlay);
            changed = true;
          });
        });
      });
      if (changed) schedule();
    });
    const observeOverlay = (overlay) => {
      if (observedOverlays.has(overlay)) return;
      observedOverlays.add(overlay);
      overlayObserver.observe(overlay, { attributes: true, attributeFilter: ['class', 'open', 'hidden', 'aria-hidden'] });
    };
    document.querySelectorAll(overlayCandidates).forEach(observeOverlay);
    overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    resizeObserver?.observe(primary);
    resizeObserver?.observe(bar);

    bar.addEventListener('transitionend', (event) => {
      if (event.target === bar && event.propertyName === 'transform' && !visible) {
        if (hideTimer) clearTimeout(hideTimer);
        finishHide();
      }
    }, { signal });

    button.addEventListener('click', onClick, { signal });
    document.addEventListener('theme:pdp:purchase', onPurchase, { signal });
    document.addEventListener('focusin', schedule, { signal });
    document.addEventListener('focusout', schedule, { signal });
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { signal });
    window.addEventListener('pageshow', schedule, { signal });
    viewport?.addEventListener('resize', schedule, { signal });
    viewport?.addEventListener('scroll', schedule, { signal });
    mobile.addEventListener('change', schedule, { signal });
    reducedMotion.addEventListener('change', () => {
      if (reducedMotion.matches && !visible && !bar.hidden) {
        if (hideTimer) clearTimeout(hideTimer);
        finishHide();
      }
      schedule();
    }, { signal });

    function destroy() {
      abort.abort();
      primaryObserver.disconnect();
      overlayObserver.disconnect();
      resizeObserver?.disconnect();
      if (frame) cancelAnimationFrame(frame);
      if (hideTimer) clearTimeout(hideTimer);
      bar.hidden = true;
      if (visible) document.body.classList.remove('theme-pdp-sticky-purchase-visible');
      document.body.style.removeProperty('--theme-pdp-sticky-purchase-height');
      controllers.delete(section);
    }

    controllers.set(section, { destroy });
    schedule();
  }

  const initAll = (scope = document) => {
    if (scope.matches?.('.theme-main-product')) init(scope);
    scope.querySelectorAll?.('.theme-main-product').forEach(init);
  };

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('.theme-main-product').forEach((section) => controllers.get(section)?.destroy());
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
})();
